/**
 * Feature E02-F04 — "Стейдж-машина и задачи проекта": stage-machine + finalize
 * + meeting-complete + archive request/response contracts.
 *
 * 1:1 port of the legacy n8n stage handling. There is NO dedicated `/advance`
 * command in the old n8n: stage movement is just `update_project { updates:{
 * stage:N } }` (project.js → advanceStage). The only place the stage is bumped
 * with GREATEST(stage, N) is the pre-kickoff email send (Email.md / branch-6:
 * `UPDATE projects SET stage = GREATEST(stage, 3)`), so a high stage is never
 * lowered when a draft is (re)sent. The archive command (Archive.md) is the
 * launch/final transition `stage=5, status='launched'` + a `project_launched`
 * event.
 *
 * Lives in its OWN file (not re-exported from the shared barrel by this agent);
 * the serial Wire pass adds the `export *` line to `packages/shared/src/index.ts`.
 *
 * IDOR invariant carried over: the actor (`created_by` of the logged event) is
 * ALWAYS the session identity supplied by the route, NEVER a body field — so it
 * is intentionally absent from every *input* schema below.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Stage domain (legacy parity)
// ---------------------------------------------------------------------------
/**
 * Real `projects.stage` values written by the stack:
 *   1 Pre-Kickoff · 2 Kick-off · 3 Generation · 4 Launched · 5 Launched (legacy).
 * Stage 5 is the launched/archived terminal that the UI collapses to 4
 * (project.js → `mapStage = s => s === 5 ? 4 : s`).
 */
export const MIN_STAGE = 1;
/** Highest stage the Next/Back UI navigates to (5 is reached only via launch). */
export const MAX_NAV_STAGE = 4;
/** Legacy launched/archived terminal stage. */
export const LAUNCHED_STAGE = 5;
/** Human stage names (project.js `stageNames`), keyed by real stage. */
export const STAGE_NAMES = {
    1: 'Pre-Kickoff',
    2: 'Kick-off',
    3: 'Generation',
    4: 'Launched',
    5: 'Launched',
};
/** Stage the pre-kickoff email send floors the project to (branch-6 GREATEST). */
export const PRE_KICKOFF_SENT_STAGE = 3;
/** A stage value accepted from a stage-change request (1..5). */
export const stageValueSchema = z.number().int().min(MIN_STAGE).max(LAUNCHED_STAGE);
// ---------------------------------------------------------------------------
// Inputs / outputs
// ---------------------------------------------------------------------------
/**
 * POST /projects/:id/advance — move the project to `stage`. Thin wrapper over
 * `update_project { updates:{ stage } }` (the UI Next/Back). The route clamps to
 * the legal 1..4 nav range; the body just carries the target.
 */
export const advanceStageInputSchema = z.object({
    stage: stageValueSchema,
    /**
     * PK-QA-1/PK-QA-3 soft-confirm override. The Pre-Kickoff(1)→Kick-off(2)
     * transition with no `analysis` artifact answers 412 `reason:'no_analysis_yet'`;
     * the client re-issues with `confirmed:true` to advance anyway. Other
     * transitions ignore this flag (no gate).
     */
    confirmed: z.boolean().optional(),
});
/**
 * POST /projects/:id/finalize — launch the project: stage=5, status='launched'.
 * Distinct from archive_project only in name; both reach the launched terminal.
 * No body fields (actor from session).
 */
export const finalizeProjectInputSchema = z.object({}).strict();
/**
 * POST /projects/:id/meeting-complete — mark the kick-off meeting done
 * (project-kickoff.js → completeMeeting): persists kickoff_notes / transcript /
 * questions into data and sets data.meeting_completed=true. `confirmed` mirrors
 * the front-end confirm() guard when notes AND transcript are empty.
 */
export const meetingCompleteInputSchema = z.object({
    kickoff_notes: z.string().optional(),
    kickoff_transcript: z.string().optional(),
    kickoff_questions: z.array(z.unknown()).optional(),
    /** Client already confirmed completing an empty meeting (notes+transcript empty). */
    confirmed: z.boolean().optional(),
});
/** POST /projects/:id/archive — archive_project (Archive.md): launched terminal. */
export const archiveProjectInputSchema = z.object({}).strict();
/**
 * Generic stage-change success — the project detail is returned so the SPA can
 * `refreshProject` (get_1_project parity). `stage` echoes the resulting stage.
 */
export const stageChangeResultSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    project_id: z.number().int(),
    stage: stageValueSchema,
    status: z.string(),
});
/** meeting-complete needs the confirm signal back to the UI when it was blocked. */
export const meetingCompleteResultSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    project_id: z.number().int(),
    meeting_completed: z.boolean(),
});
//# sourceMappingURL=project-stage.js.map